import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatCurrency';
import { Target, Plus, Pencil, Trash2, CalendarClock, Trophy, TrendingUp, Banknote } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = ['identified', 'qualification', 'proposal', 'submitted', 'evaluation', 'awarded', 'lost'];
const STAGE_CLS = {
  identified: 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400',
  qualification: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400',
  proposal: 'bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400',
  submitted: 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400',
  evaluation: 'bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
  awarded: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  lost: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400',
};
const EMPTY = { title: '', type: 'tender', client_name: '', stage: 'identified', expected_revenue: '', probability: 50, submission_deadline: '', competitors: '', outcome_notes: '', notes: '' };

const Stat = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className="!p-4" data-testid={testid}>
    <CardContent className="p-0 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div><p className="text-xl font-bold font-mono leading-tight">{value ?? '—'}</p><p className="text-xs text-muted-foreground">{label}</p></div>
    </CardContent>
  </Card>
);

export default function BizDevPage() {
  const [tenders, setTenders] = useState([]);
  const [dash, setDash] = useState(null);
  const [stageFilter, setStageFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([axios.get(`${API}/bizdev/tenders`), axios.get(`${API}/bizdev/dashboard`)]);
      setTenders(t.data); setDash(d.data);
    } catch (e) { toast.error('Failed to load business development data'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return toast.error('Title is required');
    const payload = { ...form, expected_revenue: parseFloat(form.expected_revenue) || 0,
      probability: parseInt(form.probability) || 50, submission_deadline: form.submission_deadline || null };
    try {
      if (editing) await axios.put(`${API}/bizdev/tenders/${editing.id}`, payload);
      else await axios.post(`${API}/bizdev/tenders`, payload);
      toast.success(editing ? 'Updated' : 'Created');
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await axios.delete(`${API}/bizdev/tenders/${t.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const filtered = stageFilter === 'all' ? tenders : tenders.filter(t => t.stage === stageFilter);

  return (
    <div className="space-y-6" data-testid="bizdev-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="bizdev-title">
            <Target className="h-7 w-7 text-primary" /> Business Development
          </h1>
          <p className="text-muted-foreground mt-1">Tenders · Proposals · Bid Calendar · Revenue Forecast</p>
        </div>
        <Button className="rounded-xl gap-2" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} data-testid="new-tender-btn">
          <Plus className="h-4 w-4" /> New Tender / Proposal
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={Banknote} label="Pipeline Value" value={dash ? formatCurrency(dash.pipeline_value) : null}
          tone="bg-blue-100 dark:bg-blue-500/10 text-blue-600" testid="bizdev-pipeline" />
        <Stat icon={TrendingUp} label="Weighted Forecast" value={dash ? formatCurrency(dash.forecast) : null}
          tone="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600" testid="bizdev-forecast" />
        <Stat icon={Trophy} label="Win Rate" value={dash?.win_rate != null ? `${dash.win_rate}%` : 'No data'}
          tone="bg-amber-100 dark:bg-amber-500/10 text-amber-600" testid="bizdev-winrate" />
        <Stat icon={CalendarClock} label="Overdue Bids" value={dash?.overdue_bids}
          tone="bg-rose-100 dark:bg-rose-500/10 text-rose-600" testid="bizdev-overdue" />
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 space-y-4">
          {/* Stage summary */}
          <div className="flex gap-2 flex-wrap" data-testid="bizdev-stage-chips">
            <button onClick={() => setStageFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${stageFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              All ({tenders.length})
            </button>
            {STAGES.map(s => (
              <button key={s} onClick={() => setStageFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border ${stageFilter === s ? 'ring-2 ring-primary/50' : ''} ${STAGE_CLS[s]}`}
                data-testid={`stage-chip-${s}`}>
                {s} ({dash?.by_stage?.[s]?.count ?? 0})
              </button>
            ))}
          </div>

          <Card className="!p-0">
            <CardContent className="p-0 divide-y">
              {filtered.length ? filtered.map(t => (
                <div key={t.id} className="flex items-center gap-3 p-4 hover:bg-muted/40" data-testid={`tender-row-${t.id}`}>
                  <Badge variant="outline" className="capitalize shrink-0">{t.type}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{t.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {t.client_name || 'No client'} · {formatCurrency(t.expected_revenue || 0)} · {t.probability}% prob
                      {t.submission_deadline ? ` · Due ${t.submission_deadline}` : ''}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize shrink-0 ${STAGE_CLS[t.stage]}`}>{t.stage}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setEditing(t); setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(t).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ''])) }); setShowForm(true); }} data-testid={`tender-edit-${t.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => remove(t)} data-testid={`tender-delete-${t.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )) : (
                <div className="p-10 text-center text-muted-foreground" data-testid="bizdev-empty">
                  <Target className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  No tenders or proposals yet — capture your first opportunity.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bid Calendar */}
        <Card className="!p-0 h-fit" data-testid="bid-calendar">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Bid Calendar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {dash?.bid_calendar?.length ? dash.bid_calendar.map(t => (
              <div key={t.id} className="flex items-center gap-2 py-2 border-b last:border-0">
                <div className="text-center shrink-0 w-14">
                  <p className="text-xs font-mono font-bold text-primary">{t.submission_deadline}</p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate capitalize">{t.stage} · {formatCurrency(t.expected_revenue || 0)}</p>
                </div>
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">No upcoming submission deadlines</p>}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl" data-testid="tender-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Tender / Proposal</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="tender-title-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="tender-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{['tender', 'proposal', 'rfq', 'unsolicited'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={v => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="tender-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client / Funder</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div>
              <Label>Submission Deadline</Label>
              <Input type="date" value={form.submission_deadline} onChange={e => setForm({ ...form, submission_deadline: e.target.value })} data-testid="tender-deadline-input" />
            </div>
            <div>
              <Label>Expected Revenue (R)</Label>
              <Input type="number" value={form.expected_revenue} onChange={e => setForm({ ...form, expected_revenue: e.target.value })} data-testid="tender-revenue-input" />
            </div>
            <div>
              <Label>Probability (%)</Label>
              <Input type="number" min="0" max="100" value={form.probability} onChange={e => setForm({ ...form, probability: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Competitors</Label>
              <Input value={form.competitors} onChange={e => setForm({ ...form, competitors: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="tender-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
